import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "motion/react";
import { ArrowRight, ArrowUpRight, Banknote, Car } from "lucide-react";
import { api } from "@/lib/api";
import CarCard from "@/components/CarCard";
import EditorialMarquee from "@/components/EditorialMarquee";
import LoanCalculator from "@/components/LoanCalculator";

const HERO_IMG = "/hero-car.jpg";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: 0, transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Home() {
  const [cars, setCars] = useState([]);

  useEffect(() => {
    api.get("/cars").then((r) => setCars(r.data)).catch(() => {});
  }, []);

  const headline = ["Autokauppaa", "30 vuoden", "kokemuksella."];

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <motion.img
            src={HERO_IMG}
            alt="Auto"
            initial={{ scale: 1.15 }}
            animate={{ scale: 1 }}
            transition={{ duration: 1.6, ease: [0.22, 1, 0.36, 1] }}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-[#050505]/40 to-[#050505]/70" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pb-24 pt-40 w-full">
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
            className="font-mono text-xs uppercase tracking-[0.3em] text-[#ff5a00] mb-6"
          >
            Vaihtoautojen erikoisliike
          </motion.p>
          <h1 className="font-display font-semibold text-white tracking-tight text-5xl sm:text-6xl lg:text-8xl leading-[0.95]">
            {headline.map((l, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span custom={i} variants={line} initial="hidden" animate="show" className={`block ${i === 1 ? "text-[#ff5a00]" : ""}`}>
                  {l}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.9 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <Link to="/autot" data-testid="hero-cta-cars"
              className="group inline-flex items-center gap-3 bg-[#ff5a00] text-white font-mono text-sm uppercase tracking-widest px-8 py-4 hover:bg-white hover:text-black transition-colors duration-300">
              Selaa autoja <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/rahoituslaskuri" data-testid="hero-cta-loan"
              className="inline-flex items-center gap-3 border border-white/20 text-white font-mono text-sm uppercase tracking-widest px-8 py-4 hover:border-[#ff5a00] transition-colors duration-300">
              Laske rahoitus
            </Link>
          </motion.div>
        </div>
      </section>

      <EditorialMarquee />

      {/* MANIFESTO */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {[
            { n: "01", icon: Car, t: "Ajantasainen valikoima", d: "Pidämme autovalikoimamme jatkuvasti ajan tasalla, jotta löydät meiltä aina tuoreimmat myyntiin tulleet vaihtoautot. Tutustu valikoimaan verkossa ja löydä seuraava autosi helposti." },
            { n: "02", icon: Banknote, t: "Joustava rahoitus", d: "Tarjoamme joustavia rahoitusratkaisuja erilaisiin elämäntilanteisiin. Suunnitellaan yhdessä sinulle sopiva kuukausierä ja käsiraha." },
          ].map((c, i) => (
            <motion.div key={i}
              initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.6, delay: i * 0.1 }}
              className="bg-[#050505] p-10 text-center">
              <div className="flex justify-center mb-8">
                <c.icon size={56} className="text-[#ff5a00]" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-2xl mb-3 tracking-tight">{c.t}</h3>
              <p className="text-white/60 text-sm leading-relaxed">{c.d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* FEATURED CARS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <div className="flex items-end justify-between mb-12">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#ff5a00] mb-3">Valikoima</p>
            <h2 className="font-display text-4xl md:text-5xl tracking-tight">Autot myynnissä</h2>
          </div>
          <Link to="/autot" data-testid="home-all-cars"
            className="hidden sm:inline-flex items-center gap-2 font-mono text-xs uppercase tracking-widest text-white/70 hover:text-[#ff5a00] transition-colors">
            Kaikki autot <ArrowUpRight size={16} />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cars.slice(0, 6).map((car, i) => <CarCard key={car.nettiauto_id} car={car} index={i} />)}
        </div>
        <div className="mt-12 flex justify-center">
          <Link to="/autot" data-testid="home-show-more"
            className="group inline-flex items-center gap-3 border border-white/20 text-white font-mono text-xs uppercase tracking-widest px-10 py-4 hover:border-[#ff5a00] hover:text-[#ff5a00] transition-colors duration-300">
            Näytä lisää <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </section>

      {/* LOAN CTA */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24 md:pb-32">
        <div className="mb-10">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#ff5a00] mb-3">Rahoituslaskuri</p>
          <h2 className="font-display text-4xl md:text-5xl tracking-tight">Laske kuukausierä</h2>
        </div>
        <LoanCalculator />
      </section>
    </div>
  );
}
